import type { ApiErrorBody, AuthenticatedResponse } from '@nlinks/shared';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';
const REFRESH_STORAGE_KEY = 'nlinks.refresh';

/**
 * Token handling.
 *
 * The access token lives in memory only — it dies with the tab, so it can't be
 * read back out of storage by injected script after the fact. The refresh
 * token has to survive a reload, so it goes in localStorage; the server-side
 * rotation-with-replay-detection is what limits the damage if it is stolen.
 */
let accessToken: string | null = null;
let refreshInFlight: Promise<string | null> | null = null;

const subscribers = new Set<(authenticated: boolean) => void>();

export function onAuthChange(fn: (authenticated: boolean) => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function notify(authenticated: boolean): void {
  for (const fn of subscribers) fn(authenticated);
}

export function setTokens(tokens: { accessToken: string; refreshToken: string } | null): void {
  accessToken = tokens?.accessToken ?? null;
  if (tokens) {
    localStorage.setItem(REFRESH_STORAGE_KEY, tokens.refreshToken);
  } else {
    localStorage.removeItem(REFRESH_STORAGE_KEY);
  }
  notify(Boolean(tokens));
}


export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_STORAGE_KEY);
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Array<{ path: string; message: string }>;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }

  /** Convenience for form-level error display. */
  fieldError(path: string): string | undefined {
    return this.details?.find((d) => d.path === path)?.message;
  }
}

/**
 * Exchanges the stored refresh token for a new pair. Concurrent callers share
 * one in-flight request — without this, five parallel 401s would fire five
 * rotations and four of them would be rejected as replays.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        setTokens(null);
        return null;
      }
      const data = (await response.json()) as AuthenticatedResponse;
      setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      return data.accessToken;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Set for endpoints that must not trigger a refresh attempt (login, refresh). */
  skipAuth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
  const { body, skipAuth, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  if (body !== undefined) finalHeaders.set('Content-Type', 'application/json');
  if (!skipAuth && accessToken) finalHeaders.set('Authorization', `Bearer ${accessToken}`);

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: finalHeaders,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Transparent recovery from an expired access token: refresh once, replay
  // the request, and only surface the failure if that also fails.
  if (response.status === 401 && !skipAuth && !isRetry) {
    const fresh = await refreshAccessToken();
    if (fresh) return request<T>(path, options, true);
    setTokens(null);
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json') ? await response.json() : await response.text();

  if (!response.ok) {
    const errorBody = (payload as ApiErrorBody)?.error ?? {
      code: 'INTERNAL_ERROR',
      message: typeof payload === 'string' && payload ? payload : `Request failed (${response.status})`,
    };
    throw new ApiError(response.status, errorBody);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
  /** Restores a session on page load. Returns null when there's nothing to restore. */
  bootstrap: refreshAccessToken,
};

/** Builds the SSE URL, which needs the token inline (EventSource can't set headers). */
export function eventStreamUrl(): string | null {
  if (!accessToken) return null;
  return `${API_BASE}/api/events/stream?token=${encodeURIComponent(accessToken)}`;
}
